import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@invent/wl-ui-kit-next/ui';
import { ArrowRight } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { StatusChip, type ChipTone } from '@/design';

import { errorMessage } from '@/domains/api';
import { type IAddedSurveyRouteState, addSurveyRoute, useAuth } from '@/domains/auth';
import { surveyApi } from '../api';
import type { IMyCycle, IMyCycleResponse, IMyIdentifier, IMyParticipant } from '../api/contracts';
import { formatDateTime } from '../format';
import { PORTAL_PATHS } from '../paths';
import { surveyTitle } from '../survey-labels';
import { SubmissionDownloadButton } from './submission-download';

/**
 * What the Advisor Portal's Home and My surveys both read: the account's catalogue of cycles, the
 * participants it acts for, and the numbers held about them — and the pieces both draw from it.
 *
 * **Three reads, started together and adopted separately.** The cycles carry everything the start
 * affordance offers — each cycle's `eligibleParticipants`, whom survey-api says the survey is for,
 * and a team's routing on it. The participant list is what *names* a participant: the holders of
 * numbers on the identifier card, and the row labels an account that acts for several gets — and
 * what pictures one, the start menu's photos, which `eligibleParticipants` does not carry.
 *
 * Not `await Promise.allSettled([...])`: that resolves on the slower of the three, so a
 * `/my/participants` that hangs would hold the whole page on its skeleton and hide every survey
 * the respondent already has, for the sake of a request that only names things. `apiRequest`
 * sets no timeout, so "hangs" is a real state, not a theoretical one. The identifier read is the
 * same case for the same reason: it decides whether a card asks a question nothing waits on.
 *
 * **A team no active Teams cycle lists** is found here too (`awaiting`, the owner's Q17): the
 * account's participants that are no person and that no active Teams cycle offers or holds a
 * response of, each asked for its team routing — `/my/participants` calls a team and a firm alike
 * `org`, and the routing read is what tells a team, answering null for anything else. Read once
 * the other two have landed; a failed read drops that participant's note and is logged.
 *
 * Two things neither screen decides:
 *
 * - **What is active.** `phase` is the API's answer, from cycle status alone. A cycle whose
 *   `closesAt` has passed but which staff have not closed is still active, and its drafts still
 *   accept answers — re-deriving the phase from the date here would offer a read-only screen for
 *   a writable response.
 * - **Whether a survey applies to this respondent.** survey-api decides that as of SR-213: a
 *   cycle is listed when one of the account's participants has answered that survey before,
 *   holds an identifier the survey anchors on, or completed a registration for it — and for
 *   Teams, has a routing decision. The list is rendered as it came — a filter here could only
 *   disagree with the one that decides what a start is allowed to open.
 */
export interface IMyCatalogue {
  /** `null` until the first read lands; `[]` when it failed, with `error` saying so. */
  cycles: Nullable<IMyCycle[]>;
  /** `null` until read, and when the read failed — the names then fall back to what a cycle's
   *  own rows carry. */
  participants: Nullable<IMyParticipant[]>;
  identifiers: Nullable<IMyIdentifier[]>;
  error: Nullable<string>;
  setError: (error: Nullable<string>) => void;
  /** The row as survey-api answered a confirmation — which, for a number answered elsewhere
   *  first, is not the answer pressed here — so the card for it goes and the page stays true. */
  answered: (row: IMyIdentifier) => void;
  /** The account's teams no active Teams cycle lists; `[]` until read. */
  awaiting: IMyParticipant[];
}

export function useMyCatalogue(): IMyCatalogue {
  const [cycles, setCycles] = React.useState<Nullable<IMyCycle[]>>(null);
  const [participants, setParticipants] = React.useState<Nullable<IMyParticipant[]>>(null);
  const [identifiers, setIdentifiers] = React.useState<Nullable<IMyIdentifier[]>>(null);
  const [error, setError] = React.useState<Nullable<string>>(null);

  React.useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    void surveyApi
      .myCycles(signal)
      .then((loaded) => {
        setCycles(loaded);
        setError(null);
      })
      .catch((loadError: unknown) => {
        if (signal.aborted) return;
        setCycles([]);
        setError(errorMessage(loadError, 'Could not load your surveys'));
      });

    // A failed participant read is not a page failure: the list still renders, and so does
    // every start. What goes is the names — the identifier card's holders, and the row labels
    // a multi-participant account gets, which fall back to a cycle holding two responses — and
    // the start menu's photos, which fall back to initials.
    // Logged rather than swallowed, because an unnamed page is otherwise indistinguishable
    // from one that is working.
    void surveyApi
      .myParticipants(signal)
      .then(setParticipants)
      .catch((participantsError: unknown) => {
        if (signal.aborted) return;
        setParticipants(null);
        console.error('[Survey] Could not list the account’s participants:', participantsError);
      });

    // Not a page failure either, and for a stronger reason: the question the identifiers feed
    // blocks nothing server-side, so a page that failed over it would be a gate of this app's.
    // Logged for the reason the participant read is — a card that silently never appears is
    // otherwise indistinguishable from an account with nothing to be asked.
    void surveyApi
      .myIdentifiers(signal)
      .then(setIdentifiers)
      .catch((identifiersError: unknown) => {
        if (signal.aborted) return;
        setIdentifiers(null);
        console.error('[Survey] Could not list the account’s identifiers:', identifiersError);
      });

    return () => controller.abort();
  }, []);

  const [awaiting, setAwaiting] = React.useState<IMyParticipant[]>([]);
  React.useEffect(() => {
    if (cycles === null || participants === null) return undefined;
    const candidates = unlistedOrgs(cycles, participants);
    setAwaiting([]);
    if (candidates.length === 0) return undefined;
    const controller = new AbortController();
    const { signal } = controller;
    void Promise.all(
      candidates.map((held) =>
        surveyApi.teamRouting(held.participantId, signal).then(
          (routing) => (routing === null ? null : held),
          (routingError: unknown) => {
            if (!signal.aborted) {
              console.error('[Survey] Could not read a team’s routing:', routingError);
            }
            return null;
          }
        )
      )
    ).then((teams) => {
      if (!signal.aborted) setAwaiting(teams.filter((team) => team !== null));
    });
    return () => controller.abort();
  }, [cycles, participants]);

  const answered = React.useCallback(
    (row: IMyIdentifier) =>
      setIdentifiers(
        (current) =>
          current?.map((held) => (held.identifierId === row.identifierId ? row : held)) ?? current
      ),
    []
  );

  return { cycles, participants, identifiers, error, setError, answered, awaiting };
}

/** The participants that are no person and that no active Teams cycle offers or holds a
 *  response of — the teams among them are `awaiting`. Read off the whole catalogue, not the
 *  startable cycles: a team whose Teams response is under way is listed. */
function unlistedOrgs(
  cycles: readonly IMyCycle[],
  participants: IMyParticipant[]
): IMyParticipant[] {
  const listed = new Set(
    cycles
      .filter((cycle) => cycle.phase === 'active' && cycle.survey.code === 'TEAMS')
      .flatMap((cycle) => [
        ...cycle.eligibleParticipants.map((held) => held.participantId),
        ...cycle.responses.map((response) => response.participantId)
      ])
  );
  return participants.filter((held) => held.kind !== 'person' && !listed.has(held.participantId));
}

/**
 * Whether the way to add a survey is drawn — `ADD_SURVEY_ROUTE` is an advisor's alone (the teams
 * onboarding plan's R7): survey-api binds a registration run to any session, a Team Portal
 * account's included, and a survey added to one is nothing the product means to offer. The
 * Advisor Portal's zone already turns Team Portal accounts away at its door, so inside it this is
 * always true; it is read off the session all the same, so the start card's "Register the firm"
 * and the "Add a survey" card answer to the account's kind and not to where they happen to be
 * mounted.
 */
export function useCanAdd(): boolean {
  const { user } = useAuth();
  return user?.kind === 'advisor';
}

export interface ICycleResponse {
  cycle: IMyCycle;
  response: IMyCycleResponse;
  /** Whose response this is, shown when the list names participants (`namesParticipants`) —
   *  the same fact that turns the picker on — or when one cycle carries more than one of
   *  this account's responses (a second participant, or an archived response beside its live
   *  replacement). The second condition survives on its own account: it is what keeps a row
   *  labelled when the participant read failed and the count is unknown. */
  showParticipant: boolean;
  /** The participant as the list names it (`participantLabel`). */
  participant: string;
  /** Whether the account reaches the participant as a `member` or an `assistant` — its rows
   *  then come after the account's own (the owner's Q15). */
  relayed: boolean;
}

/**
 * How the list names a participant by the account's relation to it (ruling 12, the owner's
 * Q15): "Listed on <name>" for a team or firm whose roster lists the account (`member`, PFR §5),
 * "for <name>" for the advisor an assistant acts for (`assistant`, PFR §8), the name for the rest.
 */
export function participantLabel(name: string, relation: Nullable<string> | undefined): string {
  if (relation === 'member') return `Listed on ${name}`;
  if (relation === 'assistant') return `for ${name}`;
  return name;
}

/** Whether the account reaches a participant other than as its own or a point of contact. */
export function isRelayed(relation: Nullable<string> | undefined): boolean {
  return relation === 'member' || relation === 'assistant';
}

/** Each participant's relation, from `/my/participants` — empty while it is out, or failed. */
export function relationsOf(
  participants: Nullable<IMyParticipant[]>
): ReadonlyMap<string, string> {
  return new Map(participants?.map((held) => [held.participantId, held.relation]));
}

/** Whether the list says whose each entry is: the account acts for several participants, or for
 *  one it reaches as a member or an assistant, whose entries would read as its own (Q15). */
export function namesParticipants(participants: Nullable<IMyParticipant[]>): boolean {
  if (!participants) return false;
  return participants.length > 1 || participants.some((held) => isRelayed(held.relation));
}

/** Every response the account holds, newest cycle first — the API's order, kept as it
 *  came. Flattened out of the cycles so the page renders one row per response rather than
 *  one per cycle: a cycle can carry two (a second participant, or an archived response
 *  beside its live replacement), and collapsing them would hide one. `participants` names
 *  them; null names nobody but where a cycle carries two. */
export function responsesOf(
  cycles: IMyCycle[],
  participants: Nullable<IMyParticipant[]>
): ICycleResponse[] {
  const named = namesParticipants(participants);
  const relations = relationsOf(participants);
  return cycles.flatMap((cycle) =>
    cycle.responses.map((response) => {
      const relation = relations.get(response.participantId);
      return {
        cycle,
        response,
        showParticipant: named || cycle.responses.length > 1,
        participant: participantLabel(response.participantName, relation),
        relayed: isRelayed(relation)
      };
    })
  );
}

/**
 * What a catalogue with nothing on it says — three different things since SR-213 scoped the
 * catalogue, heading and sentence together so the two cannot describe different states.
 *
 * The **empty catalogue** is the one that needed a sentence of its own. survey-api now lists
 * a cycle only for a participant entitled to that survey, and 10 586 of the 55 056 migrated
 * accounts are entitled to nothing — measured on the migrated target, not estimated. "When a
 * ranking cycle opens, its survey appears here" is false for every one of them: cycles are
 * open, and waiting produces none. That sentence stays for the case it was true of, a
 * catalogue holding only historical or already-answered cycles.
 *
 * Its way out is **Add a survey** (the teams onboarding plan's R7), said in the card itself for
 * an advisor (`EmptyCatalogueCard`): registering a number inside the account is what entitles a
 * participant to a survey. The sentence here stays about why the list is empty, not about what
 * to do.
 */
export function emptyDashboard(
  cycles: IMyCycle[],
  startable: IMyCycle[]
): { title: string; body: string } {
  if (startable.length > 0) {
    return {
      title: 'Nothing in progress',
      body: 'You have not started a survey yet. Open one of the cycles below.'
    };
  }
  if (cycles.length > 0) {
    return {
      title: 'Nothing in progress',
      body: 'When the next ranking cycle opens, its survey appears here.'
    };
  }
  return {
    title: 'Nothing to fill in yet',
    body:
      'No ranking survey is open for this account. Which surveys appear is decided from ' +
      'what our records hold about the people and firms this account speaks for — a ranking ' +
      'answered before, or a number on file that one is built from.'
  };
}

/** The empty card — `emptyDashboard`'s words, and where nothing at all is offered to an advisor,
 *  the way to add a survey in the same card rather than a second one beside it. */
export function EmptyCatalogueCard({
  empty,
  withAdd
}: {
  empty: { title: string; body: string };
  withAdd: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{empty.title}</CardTitle>
        <CardDescription>
          {empty.body}
          {withAdd && ` ${ADD_SENTENCE}`}
        </CardDescription>
      </CardHeader>
      {withAdd && (
        <CardContent>
          <AddSurveyLinks />
        </CardContent>
      )}
    </Card>
  );
}

/** What adding a survey is, in one sentence — under "Add a survey" and in the empty card. */
const ADD_SENTENCE =
  'Register another number — your own, your team’s lead advisor’s or your firm’s — and its ' +
  'survey is added to this account. No new sign-in is made.';

/** "Add a survey", a card of its own — on My surveys among the lists, on Home beside them. */
export function AddSurveyCard() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Add a survey</CardTitle>
        <CardDescription>{ADD_SENTENCE}</CardDescription>
      </CardHeader>
      <CardContent>
        <AddSurveyLinks />
      </CardContent>
    </Card>
  );
}

/** The way to the Add page, and straight to its firm's and team's runs — the list's firm and
 *  team starts (PFR §3, the owner's Q16). */
function AddSurveyLinks() {
  return (
    <div className="flex flex-wrap gap-3">
      <Button asChild variant="outline">
        <Link to={PORTAL_PATHS.addSurvey}>Add a survey</Link>
      </Button>
      <Button asChild variant="outline">
        <Link to={addSurveyRoute('RIA_FIRM')}>Start a firm survey</Link>
      </Button>
      <Button asChild variant="outline">
        <Link to={addSurveyRoute('TEAMS')}>Start a team survey</Link>
      </Button>
    </div>
  );
}

/** What `ADD_SURVEY_ROUTE` added, said once on arrival. A redirected registration says where it
 *  was sent instead: the survey it was for is then not offered to it, and the list would not say
 *  why. A number added is named as entered (the owner's Q11); which survey it offers is the
 *  list's to show. */
export function AddedNotice({ added }: { added: NonNullable<IAddedSurveyRouteState['added']> }) {
  if ('numbers' in added) {
    return (
      <Alert role="status">
        <AlertTitle>Number added</AlertTitle>
        <AlertDescription>
          {`${added.numbers.join(' and ')} ${added.numbers.length > 1 ? 'are' : 'is'} now on ` +
            'your account.'}
        </AlertDescription>
      </Alert>
    );
  }
  return (
    <Alert role="status">
      <AlertTitle>{added.redirected ? 'Registration sent on' : 'Survey added'}</AlertTitle>
      <AlertDescription>
        {added.redirected
          ? `It was registered for ${surveyTitle(added.destinationSurvey)} instead of ` +
            `${surveyTitle(added.survey)}.`
          : `${surveyTitle(added.survey)} is now on your account.`}
      </AlertDescription>
    </Alert>
  );
}

export function ResponseRow({
  row,
  onOpen
}: {
  row: ICycleResponse;
  onOpen: (response: IMyCycleResponse) => void;
}) {
  const { cycle, response, showParticipant } = row;
  const badge = statusBadge(response.status);
  const title = surveyTitle(cycle.survey.code);

  const opener = (
    <button
      type="button"
      onClick={() => onOpen(response)}
      className="flex flex-wrap items-center justify-between gap-3 rounded-[0.625rem] border border-border bg-card px-5 py-4 text-left outline-none transition-colors hover:bg-[var(--color-slight-blue)] focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span className="grid gap-1">
        <span className="font-medium text-[var(--color-royal-blue)]">{title}</span>
        <span className="text-sm text-muted-foreground">
          {cycle.cycle.code} cycle
          {showParticipant && ` · ${row.participant}`}
          {response.pathCode && ` · ${pathName(cycle, response.pathCode)}`} · last changed{' '}
          {formatDateTime(response.updatedAt)}
          {cycle.phase === 'active' && cycle.closesAt && ` · closes ${formatDateTime(cycle.closesAt)}`}
        </span>
      </span>
      <span className="flex items-center gap-3">
        <StatusChip tone={badge.tone} dot={badge.tone !== 'neutral'}>
          {badge.label}
        </StatusChip>
        <ArrowRight className="size-4 text-muted-foreground" aria-hidden />
      </span>
    </button>
  );
  if (!response.submittedAt) return opener;

  // Anything ever submitted — a past cycle's and a migrated one's too — has its PDF one click
  // away, beside the row rather than inside it: the row is itself a button (SR-497).
  const forWhom = showParticipant ? `, for ${row.participant}` : '';
  return (
    <div className="grid gap-1 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start sm:gap-2">
      {opener}
      <SubmissionDownloadButton
        responseId={response.responseId}
        surveyCode={cycle.survey.code}
        cycleCode={cycle.cycle.code}
        label="Download PDF"
        context={`of the ${title} survey, ${cycle.cycle.code} cycle${forWhom}`}
        variant="outline"
        className="justify-self-end sm:mt-3"
      />
    </div>
  );
}

export function pathName(cycle: IMyCycle, pathCode: string): string {
  return cycle.paths.find((path) => path.code === pathCode)?.name ?? pathCode;
}

/**
 * `/my/cycles` reports more than the live statuses, so the list has to say what became of a
 * response rather than only how far along it is. `archived` keeps the API's own word: a
 * euphemism would leave a respondent guessing why a survey they filled is no longer the one being
 * ranked. A recycled response is never sent to a respondent (survey-api's recycle bin design D6);
 * its label stays, as any status must render. An unrecognized status renders as itself — a
 * new one in survey-api should show up as a label to look up, not silently as "locked".
 */
export function statusBadge(status: string): { label: string; tone: ChipTone } {
  switch (status) {
    case 'draft':
      return { label: 'In progress', tone: 'info' };
    case 'submitted':
      return { label: 'Submitted', tone: 'success' };
    case 'locked':
      return { label: 'Closed', tone: 'neutral' };
    case 'recycled':
      return { label: 'Recycled', tone: 'neutral' };
    case 'archived':
      return { label: 'Archived', tone: 'neutral' };
    default:
      return { label: status, tone: 'neutral' };
  }
}
